import dayjs from "dayjs";
import * as Yup from "yup";
import {
  specialCharactersNotAllowedValidation,
  validEmailValidation,
} from "../../../helpers/constants/commonValidationSchemaUtils";
import { KeyValueMap } from "../../../redux/slices/appState/models";
import {
  CoachDetailKey,
  PowerBiReportsDetailKey,
  USER_ROLE_TYPES,
  UserDetailKey,
  UserRoleKey,
} from "./models";

export const CreateUserRoleSchema = (commonValidationMessages: KeyValueMap) =>
  Yup.object().shape({
    [UserRoleKey.userEmail]: validEmailValidation(
      commonValidationMessages.invalidEmail,
    ).required(commonValidationMessages.requiredValidation),
    [UserRoleKey.firstName]: Yup.string().default(""),
    [UserRoleKey.lastName]: Yup.string().default(""),
    [UserRoleKey.activeDirectoryId]: Yup.string().default(""),
    [UserRoleKey.role]: Yup.array().test(
      UserRoleKey.roleType,
      commonValidationMessages.requiredValidation,
      function (value) {
        const { roleType } = this.parent;
        if (
          roleType === USER_ROLE_TYPES.CIRRUS_P3_AND_CANDIDATE_PORTAL ||
          roleType === USER_ROLE_TYPES.ALL
        ) {
          return value && value.length > 0;
        }
        return true; // Skip validation for other role types
      },
    ),

    [UserRoleKey.clientPortalRole]: Yup.string().test(
      UserRoleKey.roleType,
      commonValidationMessages.requiredValidation,
      function (value) {
        const { roleType } = this.parent;
        if (
          roleType === USER_ROLE_TYPES.CLIENT_PORTAL ||
          roleType === USER_ROLE_TYPES.ALL
        ) {
          return !!(value && value.trim() !== "");
        }
        return true; // Skip validation for other role types
      },
    ),
    [UserRoleKey.country]: Yup.array().min(
      1,
      commonValidationMessages.requiredValidation,
    ),
    [UserRoleKey.servicesOffice]: Yup.array().min(
      1,
      commonValidationMessages.requiredValidation,
    ),
    [UserRoleKey.hasAccessToPowerBIReport]: Yup.boolean().default(false),
    [UserRoleKey.roleType]: Yup.string(),
  });

export const CreateUserDetailSchema = (commonValidationMessages: KeyValueMap) =>
  Yup.object().shape({
    [UserDetailKey.preferredName]: Yup.string().default(""),
    [UserDetailKey.userEmail]: Yup.string().default(""),
    [UserDetailKey.affliateEmailId]: validEmailValidation(
      commonValidationMessages.invalidLabelName,
    ).default(""),
    [UserDetailKey.userCountry]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [UserDetailKey.homeOffice]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [UserDetailKey.applicationLanguage]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [UserDetailKey.timeZone]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [UserDetailKey.userStatus]: Yup.string().default(""),
    [UserDetailKey.dateFormat]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
  });

export const CreateUserPowerBIReportsSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  Yup.object().shape({
    [PowerBiReportsDetailKey.userEmail]: Yup.string().default(""),
    [PowerBiReportsDetailKey.firstName]: Yup.string().default(""),
    [PowerBiReportsDetailKey.lastName]: Yup.string().default(""),
    [PowerBiReportsDetailKey.reports]: Yup.array().min(
      1,
      commonValidationMessages.requiredValidation,
    ),
    [PowerBiReportsDetailKey.comments]: Yup.string().nullable().default(""),
  });

const TIME_FORMAT = "HH:mm";

export const CreateUserCoachDetailSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  Yup.object().shape({
    [CoachDetailKey.bioData]: Yup.string().default(""),
    [CoachDetailKey.expertise]: Yup.string().default(""),
    [CoachDetailKey.linkedInId]: Yup.string().default(""),
    [CoachDetailKey.coachingLanguages]: specialCharactersNotAllowedValidation(
      commonValidationMessages.requiredValidation,
      commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
    ).default(""),
    [CoachDetailKey.timeZone]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CoachDetailKey.serviceAccountName]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CoachDetailKey.availableDays]: Yup.array().min(
      1,
      commonValidationMessages.requiredValidation,
    ),
    [CoachDetailKey.workingHoursStartTime]: Yup.string().required(
      commonValidationMessages.requiredValidation,
    ),
    [CoachDetailKey.workingHoursEndTime]: Yup.string()
      .required(commonValidationMessages.requiredValidation)
      .test(
        "is-greater",
        commonValidationMessages.startTimeEndTimeValidationMessage,
        function (endTime) {
          const { workingHoursStartTime } = this.parent;
          if (!workingHoursStartTime || !endTime) return true; // Skip validation if either is missing
          const start = dayjs(workingHoursStartTime, TIME_FORMAT);
          const end = dayjs(endTime, TIME_FORMAT);
          return end.isAfter(start);
        },
      ),
  });
